// ============================================================================
// SPECIALIST ADMIN QUEUE HEALTH VIEW — Admin-scoped operational dashboard
//
// Role access: specialist_admin only (route-guarded)
//
// 3 metric cards:
//   1. Seen Today          — SEEN items where seenAt = today
//   2. Needs Action        — all SEEN items (fire when past SLA > 0)
//   3. Closed Loop (7 days) — CLOSED_LOOP items where closedAt within last 7 days
//
// Scope: filtered to referral.specialtyId ∈ user.specialtyIds
//        No consultantId filter (admin sees all consultants).
//
// Data source: listSpecialistQueueItems (status-scoped: SCHEDULED/SEEN active +
//   CLOSED_LOOP/NO_SHOW terminal last 30d) — complete, not capped at newest-N.
//
// Zero-PHI invariant: this view never reads from /phi/**.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import {
  listSpecialistQueueItems,
  getImpactSettings,
  DEFAULT_IMPACT_SETTINGS,
  WORKFLOW_STATUS,
  queueSlaStatus,
} from '../../core/index.js';
import {
  Activity, RefreshCw, AlertTriangle, Flame,
  Eye, CheckCircle2, AlertCircle,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function toLocalDateStr(ts) {
  const d = tsToDate(ts);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-slate-800/30 border border-slate-700/20 rounded-2xl p-5 animate-pulse">
      <div className="h-3 w-24 bg-slate-700/60 rounded mb-3" />
      <div className="h-10 w-16 bg-slate-700/60 rounded" />
    </div>
  );
}

// Severity-driven styling. `severity` ('critical'|'warn'|'active'|'empty') comes
// from queueSlaStatus (share-of-queue) and overrides the legacy `fire` boolean.
// 'active' = breached count > 0 but below warn — still visibly shown, no fire color.
const METRIC_SEVERITY = {
  critical: { box: 'bg-red-500/10 border-red-500/20',     accent: 'text-red-400',   sub: 'text-red-400/70',   pulse: 'bg-red-500' },
  warn:     { box: 'bg-amber-500/10 border-amber-500/20', accent: 'text-amber-400', sub: 'text-amber-400/70', pulse: 'bg-amber-500' },
  active:   { box: 'bg-slate-800/50 border-slate-700/40', accent: null,             sub: 'text-slate-500',    pulse: null },
  empty:    { box: 'bg-slate-800/50 border-slate-700/40', accent: null,             sub: 'text-slate-500',    pulse: null },
};

function MetricCard({ label, value, subtitle, icon: Icon, colorTw, fire, fireLabel, onClick, severity }) {
  const tier = severity || (fire === true ? 'critical' : 'empty');
  const sev = METRIC_SEVERITY[tier] || METRIC_SEVERITY.empty;
  const isAlert = tier === 'critical' || tier === 'warn';
  const accent = sev.accent || colorTw;
  const clickable = !!onClick;
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-5 transition-all ${sev.box}
      ${clickable ? 'cursor-pointer hover:ring-1 hover:ring-slate-500/30' : ''}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      {isAlert && sev.pulse && (
        <div className={`absolute inset-0 ${sev.pulse} animate-pulse opacity-10 rounded-2xl`} />
      )}
      <div className="relative flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Icon className={`w-4 h-4 ${accent}`} />
            <p className={`text-xs font-medium ${isAlert ? accent : 'text-slate-400'}`}>
              {label}
            </p>
          </div>
          <p className={`text-4xl font-black tracking-tight ${accent}`}>
            {value}
          </p>
          {subtitle && (
            <p className={`text-xs mt-1 ${sev.sub}`}>
              {subtitle}
            </p>
          )}
          {isAlert && fireLabel && (
            <span className={`inline-flex items-center gap-1 mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${
              tier === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'
            }`}>
              <Flame className="w-3 h-3" />{fireLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function SpecialistAdminQueueHealthView({ onNavigate, activeFocusTarget, onLearningContext }) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const uid = user?.uid;
  const specialtyIds = user?.specialtyIds ?? [];
  const specialtyIdsKey = specialtyIds.slice().sort().join(',');

  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId || !uid) return;
    try {
      const [data, impactSettings] = await Promise.all([
        listSpecialistQueueItems(tenantId),
        getImpactSettings(tenantId),
      ]);
      setItems(data);
      setSettings(impactSettings || DEFAULT_IMPACT_SETTINGS);
      setError(null);
    } catch (err) {
      console.error('[SpecialistAdminQueueHealth] Failed to load:', err);
      setError('Failed to load queue data. Please retry.');
    }
  }, [tenantId, uid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (onLearningContext) {
      onLearningContext({ specialistAdminQueueHealth: true });
    }
    return () => { if (onLearningContext) onLearningContext({}); };
  }, [onLearningContext]);

  useEffect(() => {
    if (!activeFocusTarget) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFocusTarget]);

  const hl = (id) => activeFocusTarget === id ? 'ring-2 ring-emerald-400/20' : '';

  const handleRefresh = async () => {
    setRefreshing(true);
    setItems(null);
    await load();
    setRefreshing(false);
  };

  const loading = (items === null || settings === null) && !error;

  // ── Derive metrics ────────────────────────────────────────────────────────

  const metrics = useMemo(() => {
    if (!items || !settings) return null;

    const mySpecialtyIds = specialtyIdsKey ? specialtyIdsKey.split(',') : [];
    const today = todayLocal();
    const nowMs = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

    const states = settings?.sla?.states || {};
    const seenSlaMs = (states.SEEN?.slaHours ?? 72) * 60 * 60 * 1000;

    let seenTodayCount = 0;
    let pendingClosureCount = 0;
    let overdueCount = 0;
    let closedLast7DaysCount = 0;

    for (const { referral, workflow } of items) {
      if (mySpecialtyIds.length > 0 && !mySpecialtyIds.includes(referral?.specialtyId)) continue;

      const status = workflow?.status;

      if (status === WORKFLOW_STATUS.SEEN) {
        // appointmentDate is normally a 'YYYY-MM-DD' string; normalize defensively
        // in case it is ever stored as a Timestamp/Date so the compare stays valid.
        const apptRaw = workflow?.scheduling?.appointmentDate ?? null;
        const apptDate = typeof apptRaw === 'string' ? apptRaw : toLocalDateStr(apptRaw);
        if (apptDate && apptDate > today) continue;

        pendingClosureCount++;
        const seenAtMs = tsToDate(workflow?.scheduling?.seenAt)?.getTime();
        if (seenAtMs && (nowMs - seenAtMs) > seenSlaMs) {
          overdueCount++;
        }
        if (toLocalDateStr(workflow?.scheduling?.seenAt) === today) {
          seenTodayCount++;
        }
      } else if (status === WORKFLOW_STATUS.CLOSED_LOOP) {
        const d = tsToDate(workflow?.closedLoop?.closedAt);
        if (d && (nowMs - d.getTime()) <= sevenDaysMs) {
          closedLast7DaysCount++;
        }
      }
    }

    return { seenTodayCount, pendingClosureCount, overdueCount, closedLast7DaysCount };
  }, [items, settings, specialtyIdsKey]);

  // ── Severity from tenant warn/critical thresholds (share-of-queue) ──────────
  // SLA timers decide which SEEN items are overdue; queueSlaStatus decides color.
  // A breached count > 0 below warn renders 'active' so the work stays visible.
  const warnPct = settings?.sla?.thresholds?.warnPercent ?? DEFAULT_IMPACT_SETTINGS.sla.thresholds.warnPercent;
  const critPct = settings?.sla?.thresholds?.criticalPercent ?? DEFAULT_IMPACT_SETTINGS.sla.thresholds.criticalPercent;
  const needsActionSeverity = metrics
    ? (metrics.pendingClosureCount > 0
        ? (() => {
            const s = queueSlaStatus(metrics.overdueCount, metrics.pendingClosureCount, warnPct, critPct);
            return s === 'critical' ? 'critical' : s === 'warn' ? 'warn' : 'active';
          })()
        : 'empty')
    : 'empty';

  // ── Render ────────────────────────────────────────────────────────────────

  const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">

      {/* Header */}
      <div data-learning-target="specialist-admin-queue-health-header" className={`flex items-center justify-between rounded-xl transition-all ${hl('specialist-admin-queue-health-header')}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Queue Health</h1>
            <p className="text-xs text-slate-400">
              Specialist Admin — Live — {todayStr}
              {metrics && (
                <span className="ml-2 text-slate-500">
                  · {metrics.pendingClosureCount} needs action
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60
                     text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30
                        rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={handleRefresh}
            className="text-xs underline underline-offset-2 hover:text-red-300"
          >
            Retry
          </button>
        </div>
      )}

      {/* Cards */}
      {loading ? (
        <div>
          <div className="h-4 w-36 bg-slate-700/40 rounded mb-3 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </div>
      ) : metrics && (
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
            Closed Loop Health
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div data-learning-target="specialist-admin-seen-today" className={`rounded-2xl transition-all ${hl('specialist-admin-seen-today')}`}>
              <MetricCard
                label="Seen Today"
                value={metrics.seenTodayCount}
                subtitle="Referrals marked Seen today"
                icon={Eye}
                colorTw="text-teal-400"
              />
            </div>
            <div data-learning-target="specialist-admin-needs-action" className={`rounded-2xl transition-all ${hl('specialist-admin-needs-action')}`}>
              <MetricCard
                label="Needs Action"
                value={metrics.pendingClosureCount}
                subtitle="Seen referrals awaiting Closed Loop"
                icon={AlertCircle}
                colorTw="text-amber-400"
                severity={needsActionSeverity}
                fireLabel={`${metrics.overdueCount} past SLA`}
                onClick={onNavigate
                  ? () => onNavigate('specialist-admin-workplace', { tab: 'needs_close_loop' })
                  : undefined}
              />
            </div>
            <div data-learning-target="specialist-admin-closed-loop-7d" className={`rounded-2xl transition-all ${hl('specialist-admin-closed-loop-7d')}`}>
              <MetricCard
                label="Closed Loop (7d)"
                value={metrics.closedLast7DaysCount}
                subtitle="Loops closed in the last 7 days"
                icon={CheckCircle2}
                colorTw="text-emerald-400"
              />
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {metrics && metrics.seenTodayCount === 0 && metrics.pendingClosureCount === 0 &&
       metrics.closedLast7DaysCount === 0 && (
        <div className="px-4 py-3 bg-slate-800/30 border border-slate-700/20
                        rounded-xl text-slate-500 text-sm text-center">
          No referral activity found for your specialties.
        </div>
      )}

      {/* Footer */}
      <p className="text-xs text-slate-600 text-center">
        Live data from workflow states · Scoped to your specialties · All specialists included
      </p>
    </div>
  );
}
